import axios from 'axios';
// FP3-028 — Send the HTTP-only auth cookie on every request so /auth/me
// and protected endpoints work without manual header juggling.
axios.defaults.withCredentials = true;

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// FP3-035A correction — Silent token refresh.
// Access cookie expires after 60 min. Without this interceptor, a
// long scoring session (e.g. articulation screener) gets a sudden
// "Not authenticated" toast when the provider tries to save the draft
// or complete the run. Now: any 401 triggers exactly ONE refresh
// attempt; if refresh succeeds, the original request is retried; if
// it fails, the user is redirected to /login.
//
// Race-safety: concurrent 401s share a single in-flight refresh
// promise so we don't hammer /auth/refresh from a flurry of parallel
// requests (autosave + sync + viewer poll often happen simultaneously).
let _refreshInFlight = null;

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error?.config;
    const status = error?.response?.status;
    if (!original || status !== 401) throw error;
    // Don't retry the refresh endpoint itself — that would loop.
    if (original.url && original.url.includes("/auth/refresh")) throw error;
    // Don't refresh on the login or the initial /auth/me bootstrap —
    // those 401s are LEGITIMATE "no session" signals.
    if (original.url && (original.url.endsWith("/auth/login") || original.url.endsWith("/auth/me"))) {
      throw error;
    }
    if (original._retried) throw error;
    original._retried = true;
    try {
      if (!_refreshInFlight) {
        _refreshInFlight = axios
          .post(`${API_URL}/auth/refresh`, {}, { withCredentials: true })
          .finally(() => {
            // Clear after the awaited callers have all latched onto
            // the same promise.
            setTimeout(() => {
              _refreshInFlight = null;
            }, 0);
          });
      }
      await _refreshInFlight;
    } catch (refreshErr) {
      // Refresh failed → cookie is gone, route the user back to login
      // (skip the redirect for the public student-viewer route so
      // a third-party tablet doesn't get bounced).
      if (typeof window !== "undefined") {
        const path = window.location.pathname || "";
        if (!path.startsWith("/student-viewer/") && !path.startsWith("/login")) {
          window.location.href = "/login";
        }
      }
      throw error;
    }
    // Retry the original request with the freshly-minted cookie.
    return axios(original);
  },
);

// FP3-078B — Student-Data Authorization auto-recovery interceptor.
//
// Defense-in-depth backstop for the FP3-078 gate. The auth-context
// `ensureStudentDataAck()` is supposed to bracket every gated call
// site (Add Student, Import preview, …) so the modal pops BEFORE
// the request fires. But if the auth context hasn't booted yet
// (race after fresh signup) OR a caller forgot to wrap, the server
// returns 403 with `{detail: {reason: "needs_student_data_ack", ...}}`.
// Without this interceptor the caller's catch block would stringify
// the dict detail as "[object Object]" and the user would be stuck.
//
// This interceptor catches that exact 403, dispatches a window
// event that the StudentDataAckModal listens for, waits for the
// user to confirm, and then retries the original request — making
// the gate self-healing.
let _ackRetryInFlight = null;
function _isNeedsAckError(error) {
  if (!error || !error.response) return false;
  if (error.response.status !== 403) return false;
  const detail = error.response.data?.detail;
  if (!detail || typeof detail !== "object") return false;
  return detail.reason === "needs_student_data_ack";
}
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error?.config;
    if (!_isNeedsAckError(error) || !original || original._ack_retried) {
      throw error;
    }
    original._ack_retried = true;
    // Coalesce concurrent gated requests — a single ack unblocks
    // every queued caller.
    if (!_ackRetryInFlight) {
      _ackRetryInFlight = new Promise((resolve) => {
        const listener = (ev) => {
          window.removeEventListener("student-data-ack:resolved", listener);
          resolve(!!ev?.detail?.success);
        };
        window.addEventListener("student-data-ack:resolved", listener);
        window.dispatchEvent(
          new CustomEvent("student-data-ack:open", {
            detail: error.response.data.detail,
          })
        );
      }).finally(() => {
        setTimeout(() => {
          _ackRetryInFlight = null;
        }, 0);
      });
    }
    const ok = await _ackRetryInFlight;
    if (!ok) throw error;
    return axios(original);
  }
);

// FP3-078B — Safe error stringifier so we never paint
// "Failed: [object Object]" in a toast again. Accepts an axios
// error (or any thrown value) and returns a short, readable
// string suitable for `toast.error(...)`.
export function prettyApiError(err, fallback = "Something went wrong.") {
  if (!err) return fallback;
  // Axios-style error.
  const data = err.response?.data;
  if (data) {
    const detail = data.detail;
    if (typeof detail === "string" && detail.trim()) return detail;
    if (detail && typeof detail === "object") {
      const msg =
        detail.message ||
        detail.error ||
        detail.detail ||
        "";
      if (typeof msg === "string" && msg.trim()) return msg;
      // Validation errors from FastAPI come back as an array of
      // {loc, msg, type} objects.
      if (Array.isArray(detail) && detail.length > 0) {
        const first = detail[0];
        if (first && typeof first === "object") {
          const loc = Array.isArray(first.loc) ? first.loc.join(".") : "";
          const m = first.msg || first.message || "";
          if (m) return loc ? `${loc}: ${m}` : m;
        }
      }
    }
    if (typeof data === "string" && data.trim()) return data;
    if (data.message && typeof data.message === "string") return data.message;
  }
  // Plain JS Error.
  if (err.message && typeof err.message === "string" && err.message.trim()) {
    return err.message;
  }
  return fallback;
}

// FP3-044B Issue 4 — Shadow auto-injection.
// When the page URL carries `?as_provider=<pid>` (admin viewing a
// provider workspace in read-only mode), the backend already accepts
// an explicit `provider_id` query param to scope the response to
// that provider. Historically each call site had to plumb the
// shadow value through by hand, which is how Progress / Reports /
// Service Fidelity ended up showing "0 students" while Today showed
// 5 — they simply forgot to pass it. This request interceptor
// injects `provider_id` automatically on the shadow-aware endpoints
// when the URL is in shadow mode and the caller didn't pass it
// themselves. Provider users navigate without `?as_provider=`, so
// they are unaffected.
const SHADOW_AWARE_PATHS = [
  "/today",
  "/progress/caseload",
  "/students",
  "/schedule/conflicts",
  "/schedule/team",
  "/messages/threads",
  "/messages/threads/",
  "/notifications",
  "/provider/fidelity",
  "/reports",
  "/reports/dashboard",
  "/reports/coverage",
  // FP3-116 — Due Dates dashboard. Root-cause endpoint reported in
  // the P0 shadow-scope screenshot (Siara's workspace showing 849
  // missing IEP counts from every other tenant). Backend is now
  // authoritative for `?as_provider=` via `resolve_effective_shadow_user`
  // so this addition is belt-and-suspenders — it also keeps the
  // request URL cleanly self-describing (`provider_id=<pid>` shows up
  // in server logs, easing future audits).
  "/due-dates",
];

axios.interceptors.request.use((config) => {
  if (typeof window === "undefined") return config;
  try {
    const asProvider = new URLSearchParams(window.location.search || "").get("as_provider");
    if (!asProvider) return config;
    const url = String(config.url || "");
    const hit = SHADOW_AWARE_PATHS.find((p) => url.includes(`/api${p}`) || url.endsWith(p));
    if (!hit) return config;
    // Caller already pinned a provider id — respect it.
    const params = { ...(config.params || {}) };
    if (params.provider_id) return config;
    params.provider_id = asProvider;
    config.params = params;
  } catch {
    // never let a URL parse mishap break the actual request
  }
  return config;
});

// FP3-120 — Cache-invalidation response interceptor.
//
// Any successful mutation (POST / PUT / PATCH / DELETE) to endpoints
// that reshape the school-wide calendar or dashboard should invalidate
// the affected React Query caches. We keep this surgical:
//
//   • /recurring-blocks*          → schedule + dashboard
//   • /sessions* (except /log)    → schedule + dashboard
//   • /sessions/*/log             → sessionLogged (progress + dashboard)
//   • /provider-unavailability*   → schedule + dashboard + coverage
//   • /assignments*               → assignments + dashboard
//
// The interceptor is lazy-imported inside a Promise.resolve microtask
// so an import cycle with `adminQueries` never explodes at module init.
async function _fp3120_invalidateOnMutation(url = "", method = "") {
  const m = String(method || "").toLowerCase();
  if (!["post", "put", "patch", "delete"].includes(m)) return;
  const u = String(url || "");
  if (!u.startsWith(process.env.REACT_APP_BACKEND_URL + "/api/") && !u.includes("/api/")) return;
  try {
    const mod = await import("./adminQueries");
    const inv = mod.invalidateAfterMutation;
    // Session log — narrower cache set.
    if (/\/sessions\/[^/]+\/log(\b|\/|$)/.test(u)) {
      inv.sessionLogged();
      return;
    }
    if (u.includes("/recurring-blocks") || u.includes("/sessions/launch") || u.includes("/sessions/quick") || u.includes("/sessions")) {
      inv.scheduleChanged();
      return;
    }
    if (u.includes("/provider-unavailability")) {
      inv.providerHealthChanged();
      inv.scheduleChanged();
      return;
    }
    if (u.includes("/assignments")) {
      inv.providerAssignmentChanged();
      return;
    }
  } catch {
    // never let cache invalidation break the response chain
  }
}

axios.interceptors.response.use(
  (response) => {
    try {
      _fp3120_invalidateOnMutation(response?.config?.url, response?.config?.method);
    } catch {
      /* noop */
    }
    return response;
  },
  (error) => Promise.reject(error),
);


const api = {
  getProviders: () => axios.get(`${API_URL}/providers`),
  // FP3-029B4 — Shadow-aware current provider. When the page URL
  // carries `?as_provider={providerId}` (admin family entered a
  // read-only provider workspace) we substitute the shadowed
  // provider's record so every legacy caller (Schedule, Reports,
  // Notifications, Messages, Live Session) automatically scopes to
  // the selected provider instead of the seeded `is_current=True`
  // fallback (typically Sarah). The /admin/providers/{id} endpoint
  // is admin-only, so provider users with a stray `?as_provider=`
  // get a 4xx → we silently fall back to the legacy current call so
  // the page keeps working.
  getCurrentProvider: async () => {
    if (typeof window !== "undefined") {
      try {
        const asProvider = new URLSearchParams(window.location.search || "").get(
          "as_provider"
        );
        if (asProvider) {
          // FP3-069 follow-up #12 — Try the supervised-provider
          // backstop first so a non-admin supervising primary
          // (Pete viewing Max) actually gets Max's record. Falls
          // through to the admin endpoint for admin/super_admin
          // callers (who continue to use the existing path), and
          // finally to `/providers/current` only if both shadow
          // paths fail. The unconditional fall-through to /current
          // was the smoking gun behind the "banner says Max, data
          // is Pete" symptom.
          const normalize = (r) => {
            const d = r.data || {};
            return {
              ...r,
              data: {
                id: d.id,
                first_name: d.first_name,
                last_name: d.last_name,
                role: d.role,
                campus: d.campus,
                email: d.email,
                phone: d.phone,
                organization_id: d.organization_id,
                provider_role_type: d.provider_role_type,
                assistant_type: d.assistant_type,
                is_current: false,
                permissions: ["provider"],
              },
            };
          };
          try {
            return normalize(
              await axios.get(`${API_URL}/providers/${asProvider}/supervised`)
            );
          } catch {
            // Supervised lookup failed (caller does not supervise
            // this provider, or non-provider caller) — try admin.
          }
          try {
            return normalize(
              await axios.get(`${API_URL}/admin/providers/${asProvider}`)
            );
          } catch {
            // Both shadow paths failed — fall through to the legacy
            // endpoint so the page keeps working with the caller's
            // own provider record (banner will auto-hide because
            // `?as_provider=` will no longer match the resolved id).
          }
        }
      } catch {
        // window not available / URL parse error — fall through.
      }
    }
    return axios.get(`${API_URL}/providers/current`);
  },
  // Students
  getStudent: (id, params) => axios.get(`${API_URL}/students/${id}`, { params }),
  // FP3-058 — Caseload-callable archive + soft-delete (provider /
  // solo-owner / admin). The admin-only paths under /admin/students
  // continue to coexist for the Admin Students surface.
  caseloadArchiveStudent: (id, data) =>
    axios.post(`${API_URL}/students/${id}/archive`, data),
  caseloadBulkArchiveStudents: (data) =>
    axios.post(`${API_URL}/students/bulk-archive`, data),
  caseloadSoftDeleteStudent: (id, data) =>
    axios.post(`${API_URL}/students/${id}/soft-delete`, data),
  caseloadBulkSoftDeleteStudents: (data) =>
    axios.post(`${API_URL}/students/bulk-soft-delete`, data),
  // FP3-064 — Caseload bulk reassign students to a school/campus.
  // Server enforces same-org (admin family + solo OWNER); super_admin
  // can cross-org. Cross-org rows are skipped with status_code, not
  // errored, so the FE can render an "X assigned, Y skipped" summary
  // toast — same pattern as bulk-archive / bulk-soft-delete.
  caseloadBulkAssignSchool: (data) =>
    axios.post(`${API_URL}/students/bulk-assign-school`, data),
  caseloadDeletionReasons: () => axios.get(`${API_URL}/student-deletion-reasons`),
  adminExitReasons: () => axios.get(`${API_URL}/admin/students/exit-reasons`),
  // FP3-088 — Permanent Delete (admin family only). Preview classifies
  // eligible vs blocked; apply re-runs the preflight server-side.
  adminPermanentDeletePreview: (data, params) =>
    axios.post(`${API_URL}/admin/students/permanent-delete/preview`, data, { params }),
  adminPermanentDeleteApply: (data, params) =>
    axios.post(`${API_URL}/admin/students/permanent-delete/apply`, data, { params }),
  // FP3-105 — Super Admin permanent deletion of a school / organization.
  // Preview → strict confirmation → resumable server-side job.
  platformSchoolDeletePreview: (schoolId, params) =>
    axios.post(`${API_URL}/platform/schools/${schoolId}/permanent-delete/preview`, null, { params }),
  platformSchoolDeleteApply: (schoolId, data, params) =>
    axios.post(`${API_URL}/platform/schools/${schoolId}/permanent-delete/apply`, data, { params }),
  platformOrgDeletePreview: (orgId) =>
    axios.post(`${API_URL}/platform/organizations/${orgId}/permanent-delete/preview`),
  platformOrgDeleteApply: (orgId, data) =>
    axios.post(`${API_URL}/platform/organizations/${orgId}/permanent-delete/apply`, data),
  platformDeletionJob: (jobId) =>
    axios.get(`${API_URL}/platform/permanent-delete/jobs/${jobId}`),
  platformDeletionJobResume: (jobId) =>
    axios.post(`${API_URL}/platform/permanent-delete/jobs/${jobId}/resume`),
  // FP3-105A — Provider account disable / reactivate (org admin + super).
  providerDisablePreview: (providerId, params) =>
    axios.post(`${API_URL}/admin/providers/${providerId}/account/disable/preview`, null, { params }),
  providerDisable: (providerId, data, params) =>
    axios.post(`${API_URL}/admin/providers/${providerId}/account/disable`, data, { params }),
  providerReactivate: (providerId, data, params) =>
    axios.post(`${API_URL}/admin/providers/${providerId}/account/reactivate`, data, { params }),
  // FP3-105A — Super Admin permanent provider deletion.
  platformProviderDeletePreview: (providerId) =>
    axios.post(`${API_URL}/platform/providers/${providerId}/permanent-delete/preview`),
  platformProviderDeleteApply: (providerId, data) =>
    axios.post(`${API_URL}/platform/providers/${providerId}/permanent-delete/apply`, data),
  getStudents: (params) => axios.get(`${API_URL}/students`, { params }),
  createStudent: (data, { force = false } = {}) =>
    axios.post(`${API_URL}/students`, data, {
      params: force ? { force: true } : undefined,
    }),
  getGoals: (params) => axios.get(`${API_URL}/goals`, { params }),
  // FP3-126 — Session-scoped active-goal read. Live Session's
  // roster can include students the provider is co-treating in a
  // group but not on their individual caseload; the general
  // /api/goals endpoint scopes by caseload and returns [] for
  // those students. This endpoint returns
  // { goals_by_student: { <student_id>: [goals] } } for every
  // student in the session's roster, sourced from the same
  // canonical db.goals with status=active.
  getSessionGoals: (sessionId) =>
    axios.get(`${API_URL}/sessions/${sessionId}/goals`),

  // FP3-128 — Previous relevant session-note lookup (read-only).
  // Returns { previous_session: {id, date, note} | null,
  //          matched_by: "_recurring_block_id"|"individual_student"|"group_set"|null }
  // Session-scoped (never keyed on the currently-selected student),
  // so switching roster members in a group does NOT trigger a re-fetch.
  getSessionPreviousNote: (sessionId) =>
    axios.get(`${API_URL}/sessions/${sessionId}/previous-note`),
  createGoal: (data) => axios.post(`${API_URL}/goals`, data),
  updateGoal: (id, data) => axios.put(`${API_URL}/goals/${id}`, data),
  deleteGoal: (id) => axios.delete(`${API_URL}/goals/${id}`),
  generateGoalAI: (data) => axios.post(`${API_URL}/goals/ai-generate`, data),
  // FP3-020 — practice example only (manual goals + "Generate example from goal")
  generatePracticeExample: (data) =>
    axios.post(`${API_URL}/goals/ai-practice-example`, data),
  getSessions: (params) => axios.get(`${API_URL}/sessions`, { params }),
  createSession: (data) => axios.post(`${API_URL}/sessions`, data),
  updateSession: (id, data) => axios.put(`${API_URL}/sessions/${id}`, data),
  deleteSession: (id) => axios.delete(`${API_URL}/sessions/${id}`),
  getTrialRuns: (params) => axios.get(`${API_URL}/trial-runs`, { params }),
  createTrialRun: (data) => axios.post(`${API_URL}/trial-runs`, data),
  createTrialRunsBatch: (data) => axios.post(`${API_URL}/trial-runs/batch`, data),
  getNotes: (params) => axios.get(`${API_URL}/notes`, { params }),
  createNote: (data) => axios.post(`${API_URL}/notes`, data),
  getAssessments: (params) => axios.get(`${API_URL}/assessments`, { params }),
  // FP3-035 — Assessment Runner (provider-administered probes).
  listAssessmentRuns: (studentId, params) =>
    axios.get(`${API_URL}/students/${studentId}/assessment-runs`, { params }),
  getAssessmentRun: (studentId, runId) =>
    axios.get(`${API_URL}/students/${studentId}/assessment-runs/${runId}`),
  createAssessmentRun: (studentId, data) =>
    axios.post(`${API_URL}/students/${studentId}/assessment-runs`, data),
  patchAssessmentRun: (studentId, runId, data) =>
    axios.patch(`${API_URL}/students/${studentId}/assessment-runs/${runId}`, data),
  completeAssessmentRun: (studentId, runId) =>
    axios.post(`${API_URL}/students/${studentId}/assessment-runs/${runId}/complete`),
  // FP3-035B-1 — Eligibility taxonomy fetcher (public-ish endpoint;
  // any authenticated user can pull the list for Add Student forms).
  getEligibilityCategories: () =>
    axios.get(`${API_URL}/eligibility-categories`),
  // FP3-035B-1 follow-up — LRE taxonomy fetcher. Same access shape.
  getLreLevels: () =>
    axios.get(`${API_URL}/lre-levels`),
  // FP3-035B-3 — Service Fidelity Overview (admin + provider).
  // scope=auto resolves to admin for admins, provider otherwise.
  // period_type=week|month|custom; custom requires period_start +
  // period_end (YYYY-MM-DD). Filters are optional.
  getServiceFidelity: (params = {}) =>
    axios.get(`${API_URL}/service-fidelity`, { params }),
  // FP3-035C — Reference Library + norm-grounded interpretation.
  listReferenceNormSets: () =>
    axios.get(`${API_URL}/reference-library/norm-sets`),
  getReferenceNormSet: (id) =>
    axios.get(`${API_URL}/reference-library/norm-sets/${id}`),
  getAssessmentRunNormLookup: (studentId, runId) =>
    axios.get(`${API_URL}/students/${studentId}/assessment-runs/${runId}/norm-lookup`),
  generateAssessmentRunInterpretation: (studentId, runId) =>
    axios.post(`${API_URL}/students/${studentId}/assessment-runs/${runId}/interpretation`, {}),
  patchAssessmentRunInterpretation: (studentId, runId, text) =>
    axios.patch(
      `${API_URL}/students/${studentId}/assessment-runs/${runId}/interpretation`,
      { text },
    ),
  archiveAssessmentRun: (studentId, runId) =>
    axios.delete(`${API_URL}/students/${studentId}/assessment-runs/${runId}`),
  // FP3-035A — Articulation Screener templates + live viewer session.
  listScreenerTemplates: () =>
    axios.get(`${API_URL}/articulation-screener/templates`),
  getScreenerTemplate: (templateId) =>
    axios.get(`${API_URL}/articulation-screener/templates/${templateId}`),
  startScreenerSession: (data) =>
    axios.post(`${API_URL}/articulation-screener/sessions`, data),
  patchScreenerSession: (token, data) =>
    axios.patch(`${API_URL}/articulation-screener/sessions/${token}`, data),
  endScreenerSession: (token) =>
    axios.delete(`${API_URL}/articulation-screener/sessions/${token}`),
  getScreenerViewer: (token) =>
    axios.get(`${API_URL}/articulation-screener/viewer/${token}`),
  getTodayDashboard: (params) => axios.get(`${API_URL}/today`, { params }),
  // FP3-125B — Read-only freshness diagnostic. Powers the
  // "Why stale?" drawer opened from Today → Needs Attention.
  // Provider auth-scoped (own caseload · admin same-org · else 403).
  getFreshnessDebug: (studentId) =>
    axios.get(`${API_URL}/today/freshness-debug/${studentId}`),
  getRooms: (params) => axios.get(`${API_URL}/rooms`, { params }),
  getSchedule: (params) => axios.get(`${API_URL}/schedule`, { params }),
  getScheduleTeam: (params) => axios.get(`${API_URL}/schedule/team`, { params }),
  getScheduleConflicts: (params) => axios.get(`${API_URL}/schedule/conflicts`, { params }),
  getSharedSchoolCalendar: (params) => axios.get(`${API_URL}/schedule/shared-school-calendar`, { params }),
  getScheduleHealthCheck: (params) => axios.get(`${API_URL}/schedule/health-check`, { params }),
  suggestAlternativeSlots: (data) => axios.post(`${API_URL}/schedule/builder/suggest-alternative-slots`, data),
  getCaseloadProgress: (params) => axios.get(`${API_URL}/progress/caseload`, { params }),
  // FP3-130 — batched printable progress-report payload.
  // params: { provider_id?, student_ids?: "csv", start_date?: ISO, end_date?: ISO }
  // provider role → server resolves to caller's own provider_id.
  // student_ids omitted → all current caseload students.
  // student_ids given → intersect with caller's caseload only.
  getProgressReport: (params = {}) => axios.get(`${API_URL}/progress/report`, { params }),
  // Iter 40 — Admin school-wide progress oversight
  getAdminProgress: (params) => axios.get(`${API_URL}/progress/admin`, { params }),
  getStudentProgress: (id, params) => axios.get(`${API_URL}/progress/student/${id}`, { params }),
  getGoalProgress: (id, params) => axios.get(`${API_URL}/progress/goal/${id}`, { params }),
  // Schedule periods + recurring blocks
  listSchedulePeriods: (params) => axios.get(`${API_URL}/schedule-periods`, { params }),
  getActiveSchedulePeriod: (params) => axios.get(`${API_URL}/schedule-periods/active`, { params }),
  getSchedulePeriod: (id) => axios.get(`${API_URL}/schedule-periods/${id}`),
  createSchedulePeriod: (data) => axios.post(`${API_URL}/schedule-periods`, data),
  updateSchedulePeriod: (id, data) => axios.put(`${API_URL}/schedule-periods/${id}`, data),
  activateSchedulePeriod: (id) => axios.post(`${API_URL}/schedule-periods/${id}/activate`),
  deleteSchedulePeriod: (id, params) => axios.delete(`${API_URL}/schedule-periods/${id}`, { params }),
  rolloverSchedulePeriod: (id, data) => axios.post(`${API_URL}/schedule-periods/${id}/rollover`, data),
  // Admin (read-only oversight)
  // FP3-053 — Both wrappers now accept optional `params` so super
  // admin can pass `as_org` / `as_school` from the sidebar context
  // selector. Existing callers passing nothing keep working
  // (axios sends no querystring when params is undefined).
  getAdminDashboard: (params) => axios.get(`${API_URL}/admin/dashboard`, { params }),
  getAdminProviders: (params) => axios.get(`${API_URL}/admin/providers`, { params }),
  getAdminCoverageRisks: (params) => axios.get(`${API_URL}/admin/coverage-risks`, { params }),
  // FP3-053 — `params` now accepts optional `as_org` / `as_school`
  // so super admin can narrow the summary to the selected context.
  getAdminStudentsSummary: (params) =>
    axios.get(`${API_URL}/admin/students/summary`, { params }),
  getAdminStudents: (params) => axios.get(`${API_URL}/admin/students`, { params }),
  // FP3-089 — CSV export of the filtered Students list (admin family).
  // Cache hotfix: request-level no-store so authenticated CSV bytes
  // are never served from browser/proxy caches.
  adminStudentsExportCsv: (params) =>
    axios.get(`${API_URL}/admin/students/export.csv`, {
      params,
      responseType: "blob",
      headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
    }),
  // FP3-110 — CSV export of the filtered Provider Caseload. Same
  // request-level no-store contract as the admin export.
  caseloadExportCsv: (params) =>
    axios.get(`${API_URL}/caseload/export.csv`, {
      params,
      responseType: "blob",
      headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
    }),
  // Reports
  getReportsSummary: (params) => axios.get(`${API_URL}/reports/summary`, { params }),
  getReportsTrends: (params) => axios.get(`${API_URL}/reports/trends`, { params }),
  // FP3-111 — Assigned Service Minutes report.
  reportsAssignedMinutes: (params) => axios.get(`${API_URL}/reports/assigned-minutes`, { params }),
  // FP3-117 — Assigned Service Minutes · read-only contribution ledger (diagnostic).
  reportsAssignedMinutesLedger: (params) =>
    axios.get(`${API_URL}/reports/assigned-minutes/ledger`, { params }),
  reportsAssignedMinutesLedgerCsvUrl: (params = {}) => {
    const q = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== "" && v != null),
    ).toString();
    return `${API_URL}/reports/assigned-minutes/ledger.csv${q ? "?" + q : ""}`;
  },
  // Iter 44 — Admin → Provider messaging
  listMessages: (params) => axios.get(`${API_URL}/messages`, { params }),
  createMessage: (data) => axios.post(`${API_URL}/messages`, data),
  getMessageUnreadCount: (providerId) =>
    axios.get(`${API_URL}/messages/unread-count`, { params: { provider_id: providerId } }),
  markMessageRead: (id) => axios.patch(`${API_URL}/messages/${id}/read`),
  markMessageUnread: (id) => axios.patch(`${API_URL}/messages/${id}/unread`),
  // Iter 45 — Provider fidelity / minutes visibility
  getProviderFidelity: (params) => axios.get(`${API_URL}/provider/fidelity`, { params }),
  // Iter 45p — Goal Evidence + Log Session
  listGoalEvidence: (params) => axios.get(`${API_URL}/goal-evidence`, { params }),
  // FP3-069 follow-up #14 — Supervisor lens context (assistants +
  // students) independent of evidence rows so the lens shows the
  // supervision graph even when no evidence has been logged yet.
  getSupervisorLensContext: () =>
    axios.get(`${API_URL}/supervisor-lens/context`),
  createGoalEvidence: (data) => axios.post(`${API_URL}/goal-evidence`, data),
  updateGoalEvidence: (id, data) => axios.put(`${API_URL}/goal-evidence/${id}`, data),
  deleteGoalEvidence: (id) => axios.delete(`${API_URL}/goal-evidence/${id}`),
  // FP3-055 follow-up — Supervisor acknowledgement queue. Single
  // POST stamps supervisor_ack_at + supervisor_ack_by_provider_id;
  // DELETE clears them. Both gated to the row's supervising
  // provider on the backend.
  acknowledgeGoalEvidence: (id) =>
    axios.post(`${API_URL}/goal-evidence/${id}/acknowledge`),
  unacknowledgeGoalEvidence: (id) =>
    axios.delete(`${API_URL}/goal-evidence/${id}/acknowledge`),
  logSession: (sessionId, data) => axios.post(`${API_URL}/sessions/${sessionId}/log`, data),
  // FP3-114 — Edit an existing session's per-student note OR its
  // shared group note. `target` must be "student" (with student_id)
  // or "group". Does not create a duplicate session.
  updateSessionNote: (sessionId, data) =>
    axios.patch(`${API_URL}/sessions/${sessionId}/note`, data),
  // Pass B — services taxonomy (role-aware service picker)
  getServicesTaxonomy: (params) => axios.get(`${API_URL}/services/taxonomy`, { params }),
  // Iter 39 — Historical snapshot capture
  getSnapshotCoverage: () => axios.get(`${API_URL}/admin/snapshots/coverage`),
  captureSnapshotToday: () => axios.post(`${API_URL}/admin/snapshots/capture-today`),
  backfillSnapshots: (payload = {}) => axios.post(`${API_URL}/admin/snapshots/backfill`, payload),
  listRecurringBlocks: (params) => axios.get(`${API_URL}/recurring-blocks`, { params }),
  createRecurringBlock: (data) => axios.post(`${API_URL}/recurring-blocks`, data),
  updateRecurringBlock: (id, data) => axios.put(`${API_URL}/recurring-blocks/${id}`, data),
  // FP3-124 — Optional effective_date sends the provider's LOCAL
  // calendar date so historical occurrences render with the
  // pre-delete schedule instead of vanishing. Missing arg = server
  // falls back to UTC date (safe).
  deleteRecurringBlock: (id, effectiveDate) => axios.delete(
    `${API_URL}/recurring-blocks/${id}`,
    effectiveDate ? { params: { effective_date: effectiveDate } } : undefined,
  ),
  launchSessions: (data) => axios.post(`${API_URL}/sessions/launch`, data),
  // Schedule wizard
  generateScheduleDraft: (data) => axios.post(`${API_URL}/schedule/wizard/generate`, data),
  getScheduleWizardContext: (params) => axios.get(`${API_URL}/schedule/wizard/context`, { params }),
  // Schools + grade constraints (CRUD)
  // FP3-053 — Accepts optional `{ organization_id }` so super admin
  // can scope the chip strip to a single org when the context
  // selector is pinned.
  listSchools: (params) => axios.get(`${API_URL}/schools`, { params }),
  // FP3-051 — createSchool / patchSchool route through the admin
  // endpoints so the new `require_admin_or_solo_owner` dependency
  // applies. Legacy /schools POST still exists for super_admin only
  // via a different path.
  createSchool: (data) => axios.post(`${API_URL}/admin/schools`, data),
  updateSchool: (id, data) => axios.put(`${API_URL}/schools/${id}`, data),
  patchSchool: (id, data) => axios.patch(`${API_URL}/admin/schools/${id}`, data),
  // FP3-063 — Solo provider campus archive / restore / hard-delete.
  // Server enforces dependents check on delete and rejects with 409
  // when students / schedule rules / sessions / constraints are
  // still linked. Archive is non-destructive and always available
  // for admin family + solo provider owners.
  archiveSchool: (id) => axios.post(`${API_URL}/admin/schools/${id}/archive`),
  restoreSchool: (id) => axios.post(`${API_URL}/admin/schools/${id}/restore`),
  deleteSchool: (id) => axios.delete(`${API_URL}/admin/schools/${id}`),
  getSchoolDependents: (id) =>
    axios.get(`${API_URL}/admin/schools/${id}/dependents`),
  listAdminSchools: (params) =>
    axios.get(`${API_URL}/admin/schools`, { params }),
  listGradeConstraints: (params) => axios.get(`${API_URL}/grade-constraints`, { params }),
  createGradeConstraint: (data) => axios.post(`${API_URL}/grade-constraints`, data),
  updateGradeConstraint: (id, data) => axios.put(`${API_URL}/grade-constraints/${id}`, data),
  deleteGradeConstraint: (id) => axios.delete(`${API_URL}/grade-constraints/${id}`),
  // Grade schedules (per-grade × per-pattern: AM / PM / Full Day)
  listGradeSchedules: (params) => axios.get(`${API_URL}/grade-schedules`, { params }),
  createGradeSchedule: (data) => axios.post(`${API_URL}/grade-schedules`, data),
  updateGradeSchedule: (id, data) => axios.put(`${API_URL}/grade-schedules/${id}`, data),
  deleteGradeSchedule: (id) => axios.delete(`${API_URL}/grade-schedules/${id}`),
  // Flex days
  listFlexDays: (params) => axios.get(`${API_URL}/flex-days`, { params }),
  createFlexDay: (data) => axios.post(`${API_URL}/flex-days`, data),
  updateFlexDay: (id, data) => axios.put(`${API_URL}/flex-days/${id}`, data),
  deleteFlexDay: (id) => axios.delete(`${API_URL}/flex-days/${id}`),
  // Teacher-Dependent constraints (Iter 33)
  listTeacherConstraints: (params) => axios.get(`${API_URL}/teacher-constraints`, { params }),
  createTeacherConstraint: (data) => axios.post(`${API_URL}/teacher-constraints`, data),
  updateTeacherConstraint: (id, data) => axios.put(`${API_URL}/teacher-constraints/${id}`, data),
  deleteTeacherConstraint: (id) => axios.delete(`${API_URL}/teacher-constraints/${id}`),
  // Teacher directory (Iter 33b) — distinct teacher emails across students
  listTeachers: (params) => axios.get(`${API_URL}/teachers`, { params }),
  createTeacher: (data) => axios.post(`${API_URL}/teachers`, data),
  cleanupTestSchools: () => axios.post(`${API_URL}/admin/cleanup-test-schools`),
  // Provider settings (Iter 33d)
  updateProviderSettings: (provider_id, data) =>
    axios.patch(`${API_URL}/providers/${provider_id}/settings`, data),
  // Student Constraints (Iter 33b)
  listStudentConstraints: (params) => axios.get(`${API_URL}/student-constraints`, { params }),
  createStudentConstraint: (data) => axios.post(`${API_URL}/student-constraints`, data),
  updateStudentConstraint: (id, data) => axios.put(`${API_URL}/student-constraints/${id}`, data),
  deleteStudentConstraint: (id) => axios.delete(`${API_URL}/student-constraints/${id}`),
  // Session / Calendar exceptions (Iter 33b)
  listSessionExceptions: (params) => axios.get(`${API_URL}/session-exceptions`, { params }),
  createSessionException: (data) => axios.post(`${API_URL}/session-exceptions`, data),
  updateSessionException: (id, data) => axios.put(`${API_URL}/session-exceptions/${id}`, data),
  deleteSessionException: (id) => axios.delete(`${API_URL}/session-exceptions/${id}`),
  // Student demographics
  updateStudentDemographics: (id, data) => axios.patch(`${API_URL}/students/${id}`, data),
  // FP3-093 — Provider-scoped service relationship (graduate/archive
  // from MY caseload only; never touches the org-wide student record).
  updateMyAssignment: (id, data) =>
    axios.patch(`${API_URL}/students/${id}/my-assignment`, data),
  deleteStudent: (id) => axios.delete(`${API_URL}/students/${id}`),
  // Wizard apply (coordinate vs overwrite + archive)
  applyWizardDraft: (data) => axios.post(`${API_URL}/schedule/wizard/apply`, data),
  builderSave: (data) => axios.post(`${API_URL}/schedule/builder/save`, data),
  getWizardDiagnosticsLatest: () =>
    axios.get(`${API_URL}/schedule/wizard/diagnostics/latest`),
  // FP3-106C — Clear All schedule (per provider + period).
  clearAllPreview: (data) =>
    axios.post(`${API_URL}/schedule/clear-all/preview`, data),
  clearAllApply: (data) =>
    axios.post(`${API_URL}/schedule/clear-all/apply`, data),
  clearAllUndo: (data) =>
    axios.post(`${API_URL}/schedule/clear-all/undo`, data),
  // FP3-065 follow-up #7 audit — Grade Rules Check pre-flight.
  // Returns one row per (school, grade) combination touched by the
  // selected students with `status`, `constraint_count`, and
  // `warning_reason` so the Constraints step can show whether the
  // wizard would actually load the rules the operator expects.
  wizardGradeRulesCheck: (studentIds, providerId) => axios.post(
    `${API_URL}/schedule/wizard/grade-rules-check`,
    { student_ids: studentIds, provider_id: providerId },
  ),
  listScheduleArchives: (params) => axios.get(`${API_URL}/recurring-block-archives`, { params }),
  // Provider availability
  updateProviderAvailability: (id, data) => axios.patch(`${API_URL}/providers/${id}/availability`, data),
  // Iter 33d Follow-up 14k — Teacher notification foundation
  listNotificationTeachers: () => axios.get(`${API_URL}/notifications/teachers`),
  updateNotificationTeacher: (email, data) => axios.patch(`${API_URL}/notifications/teachers/${encodeURIComponent(email)}`, data),
  getNotificationSettings: (params) => axios.get(`${API_URL}/notifications/settings`, { params }),
  updateNotificationSettings: (params, data) => axios.patch(`${API_URL}/notifications/settings`, data, { params }),
  buildNotificationPreview: (data) => axios.post(`${API_URL}/notifications/preview`, data),
  sendNotifications: (data) => axios.post(`${API_URL}/notifications/send`, data),
  listNotificationLogs: (params) => axios.get(`${API_URL}/notifications/logs`, { params }),
  getNotificationLog: (id) => axios.get(`${API_URL}/notifications/logs/${id}`),
  // Iter 33d Follow-up 14l — Provider unavailability workflow
  listProviderUnavailability: (params) => axios.get(`${API_URL}/provider-unavailability`, { params }),
  previewProviderUnavailability: (data) => axios.post(`${API_URL}/provider-unavailability/preview`, data),
  commitProviderUnavailability: (data) => axios.post(`${API_URL}/provider-unavailability/commit`, data),
  deleteProviderUnavailability: (id) => axios.delete(`${API_URL}/provider-unavailability/${id}`),
  // Iter 45i — Numbers Everywhere integrity dashboard
  getAdminNumbers: (params) => axios.get(`${API_URL}/admin/numbers`, { params }),
  // FP3-046 — Quick wrappers for the Terms of Use acceptance gate.
  getTerms: () => axios.get(`${API_URL}/terms/current`),
  acceptTerms: (body) => axios.post(`${API_URL}/terms/accept`, body),
  getTermsStatus: () => axios.get(`${API_URL}/me/terms-status`),

  // FP3-015 — Read-only suspicious-sessions diagnostic surfaced on Numbers Everywhere
  getSuspiciousSessions: (params) =>
    axios.get(`${API_URL}/admin/diagnostics/suspicious-sessions`, { params }),
  // FP3-043 Phase A follow-up #8 — Campus → school_id repair.
  // GET is dry-run audit; POST is the apply. Org-scoped for
  // org-admin; super-admin can pass ?organization_id=… or omit
  // for platform-wide.
  campusLinkRepairAudit: (params) =>
    axios.get(`${API_URL}/admin/diagnostics/campus-link-repair`, { params }),
  campusLinkRepairApply: (params) =>
    axios.post(`${API_URL}/admin/diagnostics/campus-link-repair`, null, { params }),
  // Iter 45k — Role-aware Goal Area taxonomy
  getGoalAreas: (params) => axios.get(`${API_URL}/goal-areas`, { params }),
  // FP3-028 — Invite-only auth + access requests + invites + organizations + billing
  authLogin: (data) => axios.post(`${API_URL}/auth/login`, data),
  authLogout: () => axios.post(`${API_URL}/auth/logout`),
  authMe: () => axios.get(`${API_URL}/auth/me`),
  // FP3-075 — Persist the signed-in user's UX preferences (Simple
  // vs Advanced Mode, Fidelity Monitoring On/Off). Returns
  // {ok, preferences}. Either field may be omitted to update only
  // one toggle independently.
  updateAccountPreferences: (body) =>
    axios.patch(`${API_URL}/account/preferences`, body),
  authChangePassword: (data) => axios.post(`${API_URL}/auth/change-password`, data),
  // FP3-042 — Authenticated account security surface.
  getAccountMe: () => axios.get(`${API_URL}/account/me`),
  updateAccountProfile: (data) => axios.patch(`${API_URL}/account/profile`, data),
  changeAccountPassword: (data) => axios.post(`${API_URL}/account/password`, data),
  // FP3-062 follow-up — Solo provider self-set provider type from
  // Workspace Info. Backend gates this to admin family + solo
  // provider OWNER; everyone else gets 403.
  updateProviderRoleSelf: (data) =>
    axios.patch(`${API_URL}/account/provider-role`, data),
  getProviderRoleOptions: () =>
    axios.get(`${API_URL}/account/provider-role-options`),
  createAccessRequest: (data) => axios.post(`${API_URL}/access-requests`, data),
  listAccessRequests: (params) => axios.get(`${API_URL}/access-requests`, { params }),
  updateAccessRequest: (id, data) => axios.patch(`${API_URL}/access-requests/${id}`, data),
  createInvite: (data) => axios.post(`${API_URL}/invites`, data),
  listInvites: (params) => axios.get(`${API_URL}/invites`, { params }),
  resendInvite: (id) => axios.post(`${API_URL}/invites/${id}/resend`),
  acceptInvite: (data, config) => axios.post(`${API_URL}/invites/accept`, data, config),
  // FP3-028D — Organizations (super_admin sees all; org_admin sees own)
  getOrganizations: () => axios.get(`${API_URL}/organizations`),
  // FP3-030A — Admin access-request listing + status update + approval
  // FP3-053 — params accepts `as_org` for super_admin context narrowing.
  updateAccessRequest: (id, data) =>
    axios.patch(`${API_URL}/access-requests/${id}`, data),
  approveAccessRequest: (id, data) =>
    axios.post(`${API_URL}/access-requests/${id}/approve`, data),
  // FP3-030 — Forgot/reset password flow
  forgotPassword: (data) => axios.post(`${API_URL}/auth/forgot-password`, data),
  resetPassword: (data) => axios.post(`${API_URL}/auth/reset-password`, data),
  listEmailOutbox: () => axios.get(`${API_URL}/email/outbox`),
  createOrganization: (data) => axios.post(`${API_URL}/organizations`, data),
  listOrganizations: () => axios.get(`${API_URL}/organizations`),
  updateOrganization: (id, data) => axios.patch(`${API_URL}/organizations/${id}`, data),
  // FP3-110A — Super-admin-only assistant-row repair. Dry-run is
  // read-only (zero writes). Apply is a separate action guarded by
  // an exact confirm phrase + expected_stale_rows count.
  fp3110aRepairDryRun: (organizationId) =>
    axios.post(`${API_URL}/admin/repair/fp3110a-assistant-rows/dry-run`, {
      organization_id: organizationId || undefined,
    }),
  fp3110aRepairApply: (organizationId, expectedStaleRows, confirmPhrase) =>
    axios.post(`${API_URL}/admin/repair/fp3110a-assistant-rows/apply`, {
      organization_id: organizationId || undefined,
      expected_stale_rows: expectedStaleRows,
      confirm: confirmPhrase,
    }),
  // FP3-131 — Super Admin Duplicate Student Diagnostic (READ-ONLY).
  // Same shape as the CLI script; server enforces same-org scoping +
  // super_admin gate. There is intentionally NO companion mutation.
  duplicateStudentDiagnostic: ({ organization_id, first_name, last_name }) =>
    axios.get(`${API_URL}/admin/duplicate-student-diagnostic`, {
      params: { organization_id, first_name, last_name },
    }),
  // FP3-131C — READ-ONLY. Pair-specific reconciliation dry-run.
  duplicateStudentDryRun: ({ survivor_id, duplicate_ids, expected_org }) => {
    const params = new URLSearchParams();
    params.set("survivor_id", survivor_id);
    (duplicate_ids || []).forEach((d) => params.append("duplicate_id", d));
    if (expected_org) params.set("expected_org", expected_org);
    return axios.get(
      `${API_URL}/admin/duplicate-student-diagnostic/dry-run?${params.toString()}`,
    );
  },
  // FP3-131C §10 — Organization-wide duplicate scan.
  duplicateStudentOrgScan: ({ organization_id, include_deleted = false }) =>
    axios.get(`${API_URL}/admin/duplicate-student-diagnostic/org-scan`, {
      params: { organization_id, include_deleted },
    }),
  // FP3-131C §8 — Readable-ID collision audit.
  duplicateStudentReadableIdAudit: ({ organization_id }) =>
    axios.get(`${API_URL}/admin/duplicate-student-diagnostic/readable-id-audit`, {
      params: { organization_id },
    }),
  createCheckoutSession: (data) => axios.post(`${API_URL}/billing/checkout`, data),
  // FP3-037 — Super Admin Platform Control Center.
  getPlatformOverview: () => axios.get(`${API_URL}/platform/overview`),
  getPlatformOrgDetail: (id) => axios.get(`${API_URL}/platform/org/${id}`),
  // FP3-053 — Lightweight context tree feed for the Super Admin
  // sidebar selector (orgs + nested schools, names + ids only).
  getPlatformContextTree: () => axios.get(`${API_URL}/platform/context-tree`),
  // FP3-043 Phase B — Clean Pilot Org Wizard.
  pilotSetupPrecheck: (name) =>
    axios.get(`${API_URL}/platform/pilot-setup/precheck`, { params: { name } }),
  pilotSetupCreate: (data) =>
    axios.post(`${API_URL}/platform/pilot-setup`, data),
  // FP3-043 Phase C — Fresh-slate audit / apply / show-hidden toggle.
  freshSlateAudit: () =>
    axios.get(`${API_URL}/admin/diagnostics/fresh-slate-audit`),
  freshSlateApply: (confirm = false) =>
    axios.post(`${API_URL}/admin/diagnostics/fresh-slate-apply`, { confirm }),
  getShowHiddenData: () =>
    axios.get(`${API_URL}/admin/diagnostics/show-hidden-data-toggle`),
  setShowHiddenData: (showHiddenData) =>
    axios.post(`${API_URL}/admin/diagnostics/show-hidden-data-toggle`, {
      show_hidden_data: showHiddenData,
    }),
  toggleCuratedDemo: (organizationId, isCuratedDemo) =>
    axios.post(`${API_URL}/admin/diagnostics/curated-demo-toggle`, {
      organization_id: organizationId,
      is_curated_demo: isCuratedDemo,
    }),
  // FP3-049 — Per-user workspace info surface.
  getWorkspaceInfo: () => axios.get(`${API_URL}/account/workspace-info`),
  // FP3-060 — IEP / MET Due Dates hub.
  getDueDates: (params) => axios.get(`${API_URL}/due-dates`, { params }),
  // FP3-059 — Solo provider in-app assistant access request.
  // Endpoints live under /account; admin review endpoints under
  // /admin/assistant-access-requests are gated by require_admin.
  getCurrentAssistantAccessRequest: () =>
    axios.get(`${API_URL}/account/assistant-access-requests/current`),
  createAssistantAccessRequest: (data) =>
    axios.post(`${API_URL}/account/assistant-access-requests`, data),
  cancelAssistantAccessRequest: (id) =>
    axios.post(`${API_URL}/account/assistant-access-requests/${id}/cancel`),
  adminListAssistantAccessRequests: (params) =>
    axios.get(`${API_URL}/admin/assistant-access-requests`, { params }),
  adminApproveAssistantAccessRequest: (id, data) =>
    axios.post(`${API_URL}/admin/assistant-access-requests/${id}/approve`, data || {}),
  adminDeclineAssistantAccessRequest: (id, data) =>
    axios.post(`${API_URL}/admin/assistant-access-requests/${id}/decline`, data || {}),
  // FP3-069 — Solo provider self-service.
  // Direct assistant invite (writes to standard `invites` collection;
  // SuperAdmin's Access & Invites view picks it up automatically).
  inviteAssistantAsSolo: (data) =>
    axios.post(`${API_URL}/auth/invite-assistant`, data),
  // Direct Solo Provider Pilot signup (no SuperAdmin approval gate).
  signupSoloPilot: (data) =>
    axios.post(`${API_URL}/auth/signup-solo-pilot`, data),
  // FP3-029A — Student/provider assignments + CSV import batches
  listAssignments: (params) =>
    axios.get(`${API_URL}/student-provider-assignments`, { params }),
  createAssignment: (data) =>
    axios.post(`${API_URL}/student-provider-assignments`, data),
  patchAssignment: (id, data) =>
    axios.patch(`${API_URL}/student-provider-assignments/${id}`, data),
  deactivateAssignment: (id) =>
    axios.post(`${API_URL}/student-provider-assignments/${id}/deactivate`),
  // FP3-104 — canonical pair-based removal (works even when the SPA
  // mirror row is missing for a legacy/drifted assignment).
  removeAssignment: (data) =>
    axios.post(`${API_URL}/student-provider-assignments/remove`, data),
  getAdminStudentAssignments: (params) =>
    axios.get(`${API_URL}/admin/student-assignments`, { params }),
  listCsvImportBatches: (params) =>
    axios.get(`${API_URL}/students/import-csv/batches`, { params }),
  // FP3-077 — Bulk student assignment + service domain tools.
  bulkServiceDomains: (data) =>
    axios.post(`${API_URL}/admin/students/bulk/service-domains`, data),
  // FP3-085 — Areas of Impact bulk ops. Write to
  // `eligibility_categories[primary].subcategories` — NOT `service_areas`.
  // The Students-page UI uses these instead of `bulkServiceDomains`.
  bulkImpactAdd: (data) =>
    axios.post(`${API_URL}/admin/students/bulk/impact-add`, data),
  bulkImpactRemove: (data) =>
    axios.post(`${API_URL}/admin/students/bulk/impact-remove`, data),
  bulkAddProvider: (data) =>
    axios.post(`${API_URL}/admin/students/bulk/providers`, data),
  bulkRemoveProvider: (data) =>
    axios.post(`${API_URL}/admin/students/bulk/providers/remove`, data),
  // FP3-078 — Student-data authorization checkpoint (lightweight
  // gate before Add Student / Import. NOT a paid agreement).
  getStudentDataAckStatus: () =>
    axios.get(`${API_URL}/me/student-data-ack-status`),
  acknowledgeStudentData: (data) =>
    axios.post(`${API_URL}/me/student-data-ack`, data),
  // FP3-076 — Saved Import Profiles (column mapping + default
  // school) for school/district-specific CSV shapes.
  listImportProfiles: () => axios.get(`${API_URL}/import-profiles`),
  getImportProfile: (profileId) =>
    axios.get(`${API_URL}/import-profiles/${profileId}`),
  createImportProfile: (data) =>
    axios.post(`${API_URL}/import-profiles`, data),
  updateImportProfile: (profileId, data) =>
    axios.patch(`${API_URL}/import-profiles/${profileId}`, data),
  deleteImportProfile: (profileId) =>
    axios.delete(`${API_URL}/import-profiles/${profileId}`),
  // FP3-029B — Schools / campuses + provider-school assignments
  listAdminSchools: (params) =>
    axios.get(`${API_URL}/admin/schools`, { params }),
  createAdminSchool: (data) => axios.post(`${API_URL}/admin/schools`, data),
  patchAdminSchool: (id, data) =>
    axios.patch(`${API_URL}/admin/schools/${id}`, data),
  attachUserSchool: (userId, schoolId) =>
    axios.post(`${API_URL}/admin/users/${userId}/schools`, {
      school_id: schoolId,
    }),
  detachUserSchool: (userId, schoolId) =>
    axios.delete(`${API_URL}/admin/users/${userId}/schools/${schoolId}`),
  listProviderSchoolAssignments: (params) =>
    axios.get(`${API_URL}/admin/provider-school-assignments`, { params }),
  // FP3-029B3 — Provider detail + provider status
  getAdminProviderDetail: (providerId) =>
    axios.get(`${API_URL}/admin/providers/${providerId}`),
  // FP3-076A — Org Admin provider roster creation. Three endpoints:
  // create (rostered, no login yet), edit (post-create identity
  // fields), and invite (send activation email, link to existing pid).
  createRosterProvider: (data) =>
    axios.post(`${API_URL}/admin/providers`, data),
  updateRosterProvider: (providerId, data) =>
    axios.patch(`${API_URL}/admin/providers/${providerId}`, data),
  sendProviderInvite: (providerId) =>
    axios.post(`${API_URL}/admin/providers/${providerId}/invite`),
  setProviderStatus: (userId, providerStatus) =>
    axios.patch(`${API_URL}/admin/users/${userId}/provider-status`, {
      provider_status: providerStatus,
    }),
  // FP3-029B4 — Provider profile (admin-edit) + personal status
  // (provider-owned, team-facing).
  updateProviderProfile: (userId, profile) =>
    axios.patch(`${API_URL}/admin/users/${userId}/profile`, profile),
  updateMyPersonalStatus: (message) =>
    axios.patch(`${API_URL}/auth/me/personal-status`, { message }),
  // FP3-029B5 — School detail (hydrated providers + students), and
  // student-school assignment patch.
  getAdminSchoolDetail: (schoolId) =>
    axios.get(`${API_URL}/admin/schools/${schoolId}`),
  updateStudentSchool: (studentId, body) =>
    axios.patch(`${API_URL}/admin/students/${studentId}/school`, body),
  // FP3-029B6A — Route a student to a primary or assistant within
  // the supervision group. Backend validates same-org, school
  // access, and supervision-group membership.
  routeStudentToProvider: (studentId, body) =>
    axios.post(`${API_URL}/students/${studentId}/route`, body),
  // FP3-029B6A — Provider↔provider direct messaging (supervision-
  // group gated).
  listProviderMessages: (params = {}) =>
    axios.get(`${API_URL}/provider-messages`, { params }),
  sendProviderMessage: (body) =>
    axios.post(`${API_URL}/provider-messages`, body),
  // FP3-069 follow-up #13 — Mark-read / unread + unread-count for
  // provider DMs. Mirror the existing /messages/{id}/read shape so
  // the FE can dispatch by row.channel.
  markProviderMessageRead: (id) =>
    axios.patch(`${API_URL}/provider-messages/${id}/read`),
  markProviderMessageUnread: (id) =>
    axios.patch(`${API_URL}/provider-messages/${id}/unread`),
  getProviderMessageUnreadCount: () =>
    axios.get(`${API_URL}/provider-messages/unread-count`),
  // FP3-069 follow-up #14 — Supervision-aware recipient resolver for
  // the goal/student-context Message composer.
  getMessageRecipientCandidates: (params) =>
    axios.get(`${API_URL}/messages/recipient-candidates`, { params }),
  // FP3-029B6B — Self-fetch supervisor/assistant relationships.
  getMyAssistants: () => axios.get(`${API_URL}/auth/me/assistants`),
  // FP3-029C — Coverage / Reassignment workflow.
  createCoverage: (body) =>
    axios.post(`${API_URL}/admin/students/coverage`, body),
  endCoverage: (coverageId, body = {}) =>
    axios.post(
      `${API_URL}/admin/students/coverage/${coverageId}/end`,
      body
    ),
  reassignStudents: (body) =>
    axios.post(`${API_URL}/admin/students/reassign`, body),
  getStudentCoverage: (studentId) =>
    axios.get(`${API_URL}/admin/students/${studentId}/coverage`),
  // FP3-044 Phase A — Admin Student Overview aggregator + admin
  // allowlist PATCH + cross-provider fidelity rollup.
  getAdminStudentOverview: (studentId, params) =>
    axios.get(`${API_URL}/admin/students/${studentId}/overview`, { params }),
  getAdminStudentFidelityRollup: (studentId, params) =>
    axios.get(`${API_URL}/admin/students/${studentId}/fidelity-rollup`, { params }),
  // FP3-093A — Admin in-app notifications (provider ended service).
  getAdminNotifications: (params) =>
    axios.get(`${API_URL}/admin/notifications`, { params }),
  resolveAdminNotification: (id) =>
    axios.post(`${API_URL}/admin/notifications/${id}/resolve`),
  patchAdminStudent: (studentId, body) =>
    axios.patch(`${API_URL}/admin/students/${studentId}`, body),
  getProviderCoverage: (providerId, direction = "all") =>
    axios.get(
      `${API_URL}/admin/providers/${providerId}/coverage`,
      { params: { direction } }
    ),
  // FP3-031 — Shared Student Collaboration Layer.
  getStudentTeam: (studentId) =>
    axios.get(`${API_URL}/students/${studentId}/team`),
  listCollaborationNotes: (studentId) =>
    axios.get(`${API_URL}/students/${studentId}/collaboration-notes`),
  createCollaborationNote: (studentId, body) =>
    axios.post(
      `${API_URL}/students/${studentId}/collaboration-notes`,
      body
    ),
  updateCollaborationNote: (studentId, noteId, body) =>
    axios.patch(
      `${API_URL}/students/${studentId}/collaboration-notes/${noteId}`,
      body
    ),
  deleteCollaborationNote: (studentId, noteId) =>
    axios.delete(
      `${API_URL}/students/${studentId}/collaboration-notes/${noteId}`
    ),

  // FP3-032 — Supporting Documents (Student Evidence Layer).
  listStudentDocuments: (studentId, params = {}) =>
    axios.get(`${API_URL}/students/${studentId}/documents`, { params }),
  getStudentDocument: (studentId, documentId) =>
    axios.get(`${API_URL}/students/${studentId}/documents/${documentId}`),
  uploadStudentDocument: (studentId, formData) =>
    // IMPORTANT: do NOT set `Content-Type` here. When the request
    // body is a FormData instance, axios + the browser auto-set
    // `multipart/form-data; boundary=…`. Manually setting the
    // header drops the boundary and the server rejects the body
    // as malformed multipart. (FP3-032A regression fix.)
    // FP3-032C — Explicit `withCredentials: true` for safety even
    // though the global default is already set; this defends
    // against any future per-request override.
    axios.post(
      `${API_URL}/students/${studentId}/documents`,
      formData,
      { withCredentials: true }
    ),
  downloadStudentDocument: (studentId, documentId) =>
    axios.get(
      `${API_URL}/students/${studentId}/documents/${documentId}/download`,
      { responseType: "blob" }
    ),
  updateStudentDocument: (studentId, documentId, body) =>
    axios.patch(
      `${API_URL}/students/${studentId}/documents/${documentId}`,
      body
    ),
  archiveStudentDocument: (studentId, documentId) =>
    axios.post(
      `${API_URL}/students/${studentId}/documents/${documentId}/archive`
    ),
  deleteStudentDocument: (studentId, documentId) =>
    axios.delete(
      `${API_URL}/students/${studentId}/documents/${documentId}`
    ),

  // ── FP3-034 — Goal Plan / Skill Map MVP ──────────────────────────
  getGoalPlan: (studentId) =>
    axios.get(`${API_URL}/students/${studentId}/goal-plan`),
  createGoalPlanNeedArea: (studentId, body) =>
    axios.post(`${API_URL}/students/${studentId}/goal-plan/need-areas`, body),
  updateGoalPlanNeedArea: (studentId, naId, body) =>
    axios.patch(
      `${API_URL}/students/${studentId}/goal-plan/need-areas/${naId}`,
      body
    ),
  deleteGoalPlanNeedArea: (studentId, naId) =>
    axios.delete(
      `${API_URL}/students/${studentId}/goal-plan/need-areas/${naId}`
    ),
  createGoalPlanTarget: (studentId, body) =>
    axios.post(`${API_URL}/students/${studentId}/goal-plan/targets`, body),
  updateGoalPlanTarget: (studentId, tid, body) =>
    axios.patch(
      `${API_URL}/students/${studentId}/goal-plan/targets/${tid}`,
      body
    ),
  deleteGoalPlanTarget: (studentId, tid) =>
    axios.delete(`${API_URL}/students/${studentId}/goal-plan/targets/${tid}`),
  aiSuggestGoalPlan: (studentId, body) =>
    // FP3-034A — Claude can occasionally take 60–150s under retry
    // storms; raise this single axios call's timeout to 5 minutes so
    // we don't surface a confusing "AI never returned" to users.
    axios.post(
      `${API_URL}/students/${studentId}/goal-plan/ai-suggest`,
      body || {},
      { timeout: 300_000 }
    ),
  // FP3-034B — Quick "can the AI read this?" probe the picker uses
  // BEFORE calling ai-suggest. No LLM cost.
  previewGoalPlanExtraction: (studentId, body) =>
    axios.post(
      `${API_URL}/students/${studentId}/goal-plan/extraction-preview`,
      body || {}
    ),
  getGoalPlanTargetPrefill: (studentId, tid) =>
    axios.get(
      `${API_URL}/students/${studentId}/goal-plan/targets/${tid}/prefill`
    ),
  // FP3-034C — Direct goal drafter from a Goal Plan target. Used by
  // the "Generate goal" button inside Goal Details when the provider
  // arrived from the Goal Plan tab. Has the same 5-minute axios
  // timeout the ai-suggest call uses.
  draftGoalFromTarget: (studentId, tid, body) =>
    axios.post(
      `${API_URL}/students/${studentId}/goal-plan/targets/${tid}/draft-goal`,
      body || {},
      { timeout: 300_000 }
    ),

  // FP3-061 — Provider Workboard (scratch surface for referrals,
  // screenings, evaluations-in-process). Soft-deleted server-side
  // (`is_deleted` flag) but the UI treats DELETE as final.
  listWorkboardItems: (params) =>
    axios.get(`${API_URL}/workboard/items`, { params: params || {} }),
  createWorkboardItem: (body) =>
    axios.post(`${API_URL}/workboard/items`, body || {}),
  getWorkboardItem: (id) => axios.get(`${API_URL}/workboard/items/${id}`),
  updateWorkboardItem: (id, body) =>
    axios.patch(`${API_URL}/workboard/items/${id}`, body || {}),
  deleteWorkboardItem: (id) =>
    axios.delete(`${API_URL}/workboard/items/${id}`),
};

export default api;
